import { CellValueChangedEvent } from 'ag-grid-community';
import { secondaryMarketBatchApiConnector } from 'apiConnector/secondary-market/batch/api';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { cloneDeep, isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';
import { useShowToasterMessages } from 'routes/secondary-market-batch/grid/hooks/use-show-toaster-messages';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { RowData } from 'state/workspaces/secondary-market/batch/model';
import { httpClient } from 'utils/http/httpClient';
const batchApi = secondaryMarketBatchApiConnector(httpClient);
function computeModifiedFieldsBetweenRow<Data extends Record<string, unknown>>(newData: Data, oldData: Data): string[] {
  return Object.keys(newData).filter(key => !isEqual(newData[key], oldData[key]));
}

function computeModifiedFieldsBetweenRows<Data extends Record<string, unknown>>(
  newData: Record<string, Data>,
  oldData: Record<string, Data>,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.keys(newData).map(key => [key, computeModifiedFieldsBetweenRow(newData[key], oldData[key])]),
  );
}

export function useBatchUpdate(orderType: OrderType) {
  type Data = RowData;

  const dispatch = useDispatch();
  const [changes, setChanges] = useState<Record<string, Data>>({});
  const [dataBeforeChanges, setDataBeforeChanges] = useState<Record<string, Data>>({});
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showToasterMessages = useShowToasterMessages(orderType);
  const modify = useCallback(
    (value: Record<string, Data>, oldData: Record<string, Data>) => {
      const modifiedRows: Data[] = Object.values(value);
      const modifiedFields = computeModifiedFieldsBetweenRows(value, oldData);
      dispatch(secondaryMarketBatchActions.modifyRequested(orderType, modifiedRows));

      lastValueFrom(
        batchApi.modify(orderType, {
          rows: modifiedRows.map(({ metadata: _m, operationStatus: _o, rowId, ...row }) => ({
            data: { ...row, rowId },
            modifiedFields: modifiedFields[rowId] ?? [],
          })),
        }),
      )
        .then(response => {
          showToasterMessages(response.rows);
          dispatch(secondaryMarketBatchActions.modifySucceeded(orderType, response));
        })
        .catch(error => {
          dispatch(secondaryMarketBatchActions.modifyFailed(orderType, Object.values(oldData), error));
        });
    },
    [dispatch, orderType, showToasterMessages],
  );

  useEffect(() => {
    if (Object.keys(changes).length === 0) {
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      modify(changes, dataBeforeChanges);
      setChanges({});
      setDataBeforeChanges({});
    }, 2_000);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [changes, dataBeforeChanges, modify]);

  const onDataChanged = useCallback(
    <TField extends keyof Data>(
      rowId: Data['rowId'],
      row: Data,
      field: TField,
      oldValue: Data[TField],
      newValue: Data[TField],
    ) => {
      if (!dataBeforeChanges[rowId]) {
        setDataBeforeChanges(prev => {
          const oldValues = { ...prev, [rowId]: cloneDeep({ ...row, [field]: oldValue }) };
          return oldValues;
        });
      }
      setChanges(prev => {
        const changes = { ...prev, [rowId]: cloneDeep({ ...row, [field]: newValue }) };
        return changes;
      });
    },
    [dataBeforeChanges],
  );

  const onCellValueChanged = useCallback(
    ({ data, colDef: { field }, oldValue, newValue }: CellValueChangedEvent<Data>) => {
      if (!field || !data.rowId || isEqual(oldValue, newValue)) {
        return;
      }
      onDataChanged(data.rowId, data, field as keyof Data, oldValue, newValue);
    },
    [onDataChanged],
  );
  return { onCellValueChanged };
}
